/**
 * INT-0026 — pure state helpers for the schedule-import preview screen.
 * The preview is fully editable (grilling #2); these functions keep the
 * teams and games lists consistent (renames propagate, deletions cascade)
 * and decide when the draft is committable. Unit-tested.
 */

export interface DraftTeam {
  name: string;
  matchedTeamId: string | null;
}

export interface DraftGame {
  homeTeam: string;
  awayTeam: string;
  /** Playoff reference text ("Winner of SF1") when the side has no team. */
  homePlaceholder?: string | null;
  awayPlaceholder?: string | null;
  /** Game schema playoffRound value; null/absent = regular season. */
  playoffRound?: string | null;
  date: string | null;
  time: string | null;
  venue: string | null;
  /** INT-0053: printed game number and pool-play pool, carried to the commit. */
  gameNumber?: number | null;
  pool?: string | null;
  homeScore: number | null;
  awayScore: number | null;
  duplicate?: boolean;
}

export interface Draft {
  teams: DraftTeam[];
  games: DraftGame[];
}

const norm = (s: any) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

/** Rename a team; every game referencing the old name follows. */
export function renameTeam(draft: Draft, index: number, newName: string): Draft {
  const oldName = draft.teams[index]?.name;
  if (oldName === undefined) return draft;
  const oldNorm = norm(oldName);
  return {
    teams: draft.teams.map((t, i) => (i === index ? { ...t, name: newName } : t)),
    games: draft.games.map((g) => ({
      ...g,
      homeTeam: norm(g.homeTeam) === oldNorm ? newName : g.homeTeam,
      awayTeam: norm(g.awayTeam) === oldNorm ? newName : g.awayTeam,
    })),
  };
}

/** Delete a team; its games go with it (a game can't keep a ghost side).
 *  Placeholder sides don't reference teams, so they never cascade. */
export function deleteTeam(draft: Draft, index: number): Draft {
  const name = draft.teams[index]?.name;
  if (name === undefined) return draft;
  const n = norm(name);
  return {
    teams: draft.teams.filter((_, i) => i !== index),
    games: draft.games.filter((g) =>
      (g.homePlaceholder || norm(g.homeTeam) !== n) && (g.awayPlaceholder || norm(g.awayTeam) !== n)
    ),
  };
}

export function updateGame(draft: Draft, index: number, patch: Partial<DraftGame>): Draft {
  return {
    ...draft,
    games: draft.games.map((g, i) => (i === index ? { ...g, ...patch } : g)),
  };
}

export function deleteGame(draft: Draft, index: number): Draft {
  return { ...draft, games: draft.games.filter((_, i) => i !== index) };
}

/** Games the commit will skip are excluded from the import counts. */
export function importableGames(draft: Draft): DraftGame[] {
  return draft.games.filter((g) => !g.duplicate);
}

/**
 * Row-level problems that BLOCK commit (spec: unresolved dates block their
 * row; team refs must resolve). Returns [] when the draft is committable.
 */
export function validateDraft(draft: Draft): { gameIndex: number; message: string }[] {
  const teamNorms = new Set(draft.teams.map((t) => norm(t.name)));
  const errors: { gameIndex: number; message: string }[] = [];
  draft.games.forEach((g, i) => {
    if (g.duplicate) return; // skipped anyway
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(g.date || ''))) {
      errors.push({ gameIndex: i, message: 'Needs a date' });
    }
    // A side is valid as a team from the list OR a non-empty playoff
    // placeholder ("Winner of SF1").
    const homeOk = g.homePlaceholder ? !!String(g.homePlaceholder).trim() : teamNorms.has(norm(g.homeTeam));
    const awayOk = g.awayPlaceholder ? !!String(g.awayPlaceholder).trim() : teamNorms.has(norm(g.awayTeam));
    if (!homeOk || !awayOk) {
      errors.push({ gameIndex: i, message: 'Team not in the list' });
    }
    if (!g.homePlaceholder && !g.awayPlaceholder && norm(g.homeTeam) === norm(g.awayTeam)) {
      errors.push({ gameIndex: i, message: 'A team cannot play itself' });
    }
    // StandingPage counts placeholder games without a playoffRound into the
    // regular-season standings — block until the round is set (or row removed).
    if ((g.homePlaceholder || g.awayPlaceholder) && !g.playoffRound) {
      errors.push({ gameIndex: i, message: 'Playoff game needs a round' });
    }
  });
  for (const t of draft.teams) {
    if (!String(t.name).trim()) {
      errors.push({ gameIndex: -1, message: 'A team has an empty name' });
      break;
    }
  }
  return errors;
}
